import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../hooks/useContent';
import { contactAPI } from '../../services/api';
import { getContactOfficeAnchorId } from '../../utils/contactOfficeAnchor';
import {
  footerCategoryItemHref,
  getFooterItemTrekSlugs,
  parseFooterEventCategoryGroups
} from '../../utils/footerCategoryGroups';
import { parseFooterQuickLinks, resolveBrandLogoUrl } from '../../utils/siteSettingsJson';
import { resolveOfficeWhatsAppHref } from '../../utils/resolveOfficeWhatsAppHref';
import './Footer.css';

function isAhmedabadHeadOffice(office) {
  const c = String(office.city || '').toLowerCase();
  const n = String(office.officeName || '').toLowerCase();
  return c.includes('ahmedabad') || n.includes('ahmedabad') || n.includes('head office');
}

function regionalOfficeFooterLabel(office) {
  const city = String(office.city || '').trim();
  if (/^vadodara$/i.test(city)) return 'Baroda';
  return city || String(office.officeName || '').trim() || 'Office';
}

/** Break glued office hours (e.g. "...6 PM Saturday...") onto separate lines for readability */
function normalizeContactHoursForDisplay(raw) {
  let s = String(raw ?? '')
    .replace(/\r\n/g, '\n')
    .trim();
  if (!s) return '';
  if (s.includes('\n')) {
    return s
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n');
  }
  const day = 'Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday';
  let prev;
  let guard = 0;
  do {
    prev = s;
    s = s.replace(new RegExp(`([AP]M|Closed)(\\s*)(?=(?:${day})\\b)`, 'gi'), '$1\n');
    s = s.replace(new RegExp(`([AP]M|Closed)(?=(?:${day})\\b)`, 'gi'), '$1\n');
    guard += 1;
  } while (s !== prev && guard < 12);
  return s.replace(/\n{2,}/g, '\n').trim();
}

const Footer = () => {
  const { settings } = useSiteSettings();
  const brandName = String(settings?.site_brand_name || 'INVINCIBLE').trim() || 'INVINCIBLE';
  const [footerOffices, setFooterOffices] = useState([]);
  const [shareHint, setShareHint] = useState('');

  useEffect(() => {
    let cancelled = false;
    contactAPI
      .getOffices()
      .then((res) => {
        if (!cancelled) setFooterOffices(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFooterOffices([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const regionalOffices = footerOffices.filter((o) => !isAhmedabadHeadOffice(o));

  const handleFooterShare = async () => {
    setShareHint('');
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = typeof document !== 'undefined' ? document.title : brandName;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title, text: title, url });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return;
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareHint('Link copied');
        window.setTimeout(() => setShareHint(''), 2200);
        return;
      }
    } catch {
      /* fall through */
    }
    window.prompt('Copy this page link:', url);
  };
  const mission = settings.footer_mission || 'Empowering Youth Through Adventure. Connecting Young Minds With Nature Through Sustainable Trekking And Community Initiatives.';
  const copyright = settings.footer_copyright || `© ${new Date().getFullYear()} Invincible. All rights reserved.`;
  const brandLogoSrc = resolveBrandLogoUrl(settings, 'footer');
  const quickLinks = parseFooterQuickLinks(settings.footer_quick_links);
  const contactAddress = settings.contact_address || '308, University Plaza, Above Chocolate Room, Vijay Cross Roads, Navrangpura, Ahmedabad, Gujarat 380009';
  const contactHoursRaw = settings.contact_hours || '10AM TO 8PM';
  const contactHoursDisplay = normalizeContactHoursForDisplay(contactHoursRaw);
  const contactPhones = String(settings.contact_phone || '')
    .split('\n')
    .map((phone) => phone.trim())
    .filter(Boolean);
  const contactEmails = String(settings.contact_email || '')
    .split('\n')
    .map((email) => email.trim())
    .filter(Boolean);
  const primaryPhones = contactPhones.length > 0 ? contactPhones : ['+919099400699', '+919913800699'];
  const socialInstagram = settings.social_instagram || 'https://instagram.com';
  const socialLinkedIn = settings.social_linkedin || 'https://linkedin.com';
  const socialFacebook = settings.social_facebook || 'https://facebook.com';
  const socialWhatsApp = resolveOfficeWhatsAppHref(settings.contact_phone);
  const socialYouTube =
    String(settings.social_youtube || '').trim() || 'https://www.youtube.com/@Invinciblengo';
  const footerMailto = contactEmails[0]
    ? `mailto:${contactEmails[0]}`
    : 'mailto:info@invinciblengo.com';
  const footerEventCategoryGroups = parseFooterEventCategoryGroups(
    settings.footer_events_categories
  );

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Events by Category - rounded card (design only) */}
        <div className="footer-categories-card">
          <div className="footer-categories-card-inner">
            <h3 className="footer-categories-title">Events by Category</h3>
            <div className="categories-grid">
              {footerEventCategoryGroups.map((group) => (
                <div className="category-column" key={group.heading}>
                  <h4 className="category-heading">{group.heading}</h4>
                  <ul className="category-list">
                    {group.items.map((item) => {
                      const slugs = getFooterItemTrekSlugs(item);
                      const to = footerCategoryItemHref(item);
                      const key = slugs.length
                        ? `treks-${slugs.join(',')}`
                        : `${item.label}-${item.search}`;
                      return (
                        <li key={key}>
                          <Link to={to}>{item.label}</Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Footer: Logo+Mission+Social | Quick Links | Contact */}
        <div className="footer-main">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src={brandLogoSrc} alt={`${brandName} logo`} className="footer-logo-image" />
            </div>
            <p className="footer-mission">{mission}</p>
            <div className="social-icons">
              <a href={socialInstagram} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href={socialFacebook} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href={socialYouTube} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                  <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                </svg>
              </a>
              <a href={socialLinkedIn} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
              <a href={socialWhatsApp} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="WhatsApp">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.95L0 24l6.33-1.66a11.88 11.88 0 0 0 5.72 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.44-8.42zM12.06 21.8h-.01a9.9 9.9 0 0 1-5.05-1.39l-.36-.21-3.75.98 1-3.66-.23-.38a9.86 9.86 0 0 1-1.51-5.24c0-5.47 4.45-9.92 9.92-9.92 2.65 0 5.14 1.03 7.01 2.9a9.85 9.85 0 0 1 2.9 7.02c0 5.47-4.45 9.92-9.92 9.92zm5.44-7.43c-.3-.15-1.78-.88-2.05-.98-.27-.1-.47-.15-.67.15-.2.3-.77.98-.95 1.18-.17.2-.35.23-.65.08-.3-.15-1.27-.47-2.42-1.5a9.1 9.1 0 0 1-1.67-2.08c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.53.08-.8.38-.27.3-1.03 1-1.03 2.44 0 1.44 1.05 2.84 1.2 3.03.15.2 2.05 3.13 4.97 4.39.69.3 1.23.48 1.65.61.69.22 1.32.19 1.82.12.55-.08 1.78-.73 2.03-1.44.25-.71.25-1.32.18-1.44-.08-.13-.27-.2-.57-.35z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-column footer-column-quick-links">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links footer-links-quick">
              {quickLinks.map((item) => {
                const external = /^https?:\/\//i.test(item.path);
                return (
                  <li key={`${item.path}-${item.label}`}>
                    {external ? (
                      <a href={item.path} target="_blank" rel="noopener noreferrer">
                        {item.label}
                      </a>
                    ) : (
                      <Link to={item.path}>{item.label}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">Contact</h4>
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </span>
                <span>{contactAddress}</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </span>
                <span className="footer-contact-hours-text">{contactHoursDisplay}</span>
              </div>
              {primaryPhones.map((phone) => {
                const phoneHref = `tel:${phone.replace(/\s+/g, '')}`;
                return (
                  <div className="contact-item" key={phone}>
                    <span className="contact-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    </span>
                    <a href={phoneHref}>{phone}</a>
                  </div>
                );
              })}
              {contactEmails.map((email) => (
                <div className="contact-item" key={email}>
                  <span className="contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </span>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              ))}
              <div className="contact-item">
                {regionalOffices.length > 0 ? (
                  <Link className="contact-link" to={`/contact#${getContactOfficeAnchorId(regionalOffices[0])}`}>
                    {settings.footer_regional_link_text || 'Contact For Rajkot, Baroda, Surat Office →'}
                  </Link>
                ) : (
                  <Link className="contact-link" to="/contact/regional">
                    {settings.footer_regional_link_text || 'Contact For Rajkot, Baroda, Surat Office →'}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p className="copyright">{copyright}</p>
          <div className="footer-policies">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms &amp; Conditions</Link>
          </div>
          <div className="footer-share">
            {shareHint ? <span className="footer-share-hint">{shareHint}</span> : null}
            <button type="button" className="share-icon" aria-label="Share this page" onClick={handleFooterShare}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
            </button>
            <a href={footerMailto} className="share-icon" aria-label="Email">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
